// src/components/Account/PrivacyAgreementModal.js - Complete Fixed Version
import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import logger from '../../utils/logger';
import './ModernAccountStyles.css';

/**
 * Modal that appears for first-time users to accept privacy policy and terms
 * Enhanced with better user state checking and loading states
 */
const PrivacyAgreementModal = () => {
	const {
		user,
		updateUserPrivacyAcceptance,
		isLoading: authLoading,
		refreshUserProfile,
		isProfileComplete
	} = useAuth();

	const [show, setShow] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [hasAcceptedPrivacy, setHasAcceptedPrivacy] = useState(false);
	const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
	const [debugMode, setDebugMode] = useState(false); // For development debugging

	// Enhanced logic to determine if modal should be shown
	useEffect(() => {
		// Don't show modal while auth is still loading
		if (authLoading) {
			logger.info('Privacy modal: Auth still loading, waiting...');
			return;
		}

		// Don't show modal if no user is logged in
		if (!user) {
			logger.info('Privacy modal: No user logged in, hiding modal');
			setShow(false);
			return;
		}

		// Log current user state for debugging
		logger.info('Privacy modal: Checking user privacy status', {
			userId: user.id,
			email: user.email,
			privacyPolicyAccepted: user.privacyPolicyAccepted,
			termsOfServiceAccepted: user.termsOfServiceAccepted,
			privacyType: typeof user.privacyPolicyAccepted,
			termsType: typeof user.termsOfServiceAccepted,
			hasPrivacyField: user.hasOwnProperty('privacyPolicyAccepted'),
			hasTermsField: user.hasOwnProperty('termsOfServiceAccepted'),
			profileLoadError: user.profileLoadError,
			profileComplete: isProfileComplete ? isProfileComplete() : 'function not available'
		});

		// Handle profile loading errors - try to refresh profile
		if (user.profileLoadError && refreshUserProfile) {
			logger.info('Privacy modal: Profile load error detected, attempting refresh...');

			// Don't show modal yet, try to refresh profile first
			setTimeout(async () => {
				try {
					await refreshUserProfile();
					logger.info('Privacy modal: Profile refresh successful');
				} catch (refreshError) {
					logger.error('Privacy modal: Profile refresh failed', refreshError);
					// If refresh fails, show modal with current state
					checkAndShowModal();
				}
			}, 1000);

			return; // Exit early while we try to refresh
		}

		checkAndShowModal();

		function checkAndShowModal() {
			// Comprehensive privacy policy checking
			const privacyAccepted = determinePrivacyStatus(user.privacyPolicyAccepted);
			const termsAccepted = determinePrivacyStatus(user.termsOfServiceAccepted);

			// Show modal only if either policy hasn't been explicitly accepted
			const needsAcceptance = !privacyAccepted || !termsAccepted;

			logger.info('Privacy modal: Policy acceptance status', {
				privacyAccepted,
				termsAccepted,
				needsAcceptance,
				willShowModal: needsAcceptance
			});

			if (needsAcceptance) {
				setShow(true);
				setHasAcceptedPrivacy(privacyAccepted);
				setHasAcceptedTerms(termsAccepted);
			} else {
				setShow(false);
			}
		}

	}, [user, authLoading, refreshUserProfile, isProfileComplete]);

	// Helper function to determine privacy status with better logic
	const determinePrivacyStatus = (policyValue) => {
		// Explicitly accepted (true)
		if (policyValue === true) return true;

		// Explicitly rejected or not set (false, null, undefined)
		return false;
	};

	const handleAcceptAll = async () => {
		try {
			setLoading(true);
			setError('');

			logger.info('Privacy modal: User accepting policies', {
				userId: user?.id,
				email: user?.email
			});

			// Call API to record user's acceptance
			const response = await api.post('/users/accept-policies', {
				privacyPolicyAccepted: true,
				termsOfServiceAccepted: true,
				acceptedAt: new Date().toISOString()
			});

			if (response.data.success) {
				logger.info('Privacy modal: API acceptance successful', response.data);

				// Update user context
				if (updateUserPrivacyAcceptance) {
					updateUserPrivacyAcceptance(true, true);
				}

				// Also refresh the complete profile to ensure we have the latest data
				if (refreshUserProfile) {
					try {
						await refreshUserProfile();
						logger.info('Privacy modal: Profile refreshed after acceptance');
					} catch (refreshError) {
						logger.warn('Privacy modal: Profile refresh after acceptance failed', refreshError);
						// Don't fail the whole process if refresh fails
					}
				}

				// Close modal
				setShow(false);
				logger.info('Privacy modal: User accepted privacy policy and terms of service');
			} else {
				throw new Error(response.data.message || 'Failed to record acceptance');
			}
		} catch (err) {
			logger.error('Privacy modal: Error accepting policies:', err);

			// Show user-friendly error message
			let errorMessage = 'Failed to record your acceptance. Please try again.';
			if (err.response?.status === 400) {
				errorMessage = 'Invalid request. Please refresh the page and try again.';
			} else if (err.response?.status >= 500) {
				errorMessage = 'Server error. Please try again in a few moments.';
			}

			setError(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	const handleViewPrivacyPolicy = () => {
		window.open('/privacy-policy', '_blank');
	};

	const handleViewTermsOfService = () => {
		window.open('/terms-of-service', '_blank');
	};

	// Debug function for development
	const handleDebugToggle = () => {
		if (process.env.NODE_ENV === 'development') {
			setDebugMode(!debugMode);
		}
	};

	const handleForceRefreshProfile = async () => {
		if (refreshUserProfile) {
			try {
				setError('');
				await refreshUserProfile();
				logger.info('Privacy modal: Manual profile refresh completed');
			} catch (error) {
				logger.error('Privacy modal: Manual profile refresh failed', error);
				setError('Failed to refresh profile data.');
			}
		}
	};

	// Don't render anything while auth is loading
	if (authLoading) {
		return null;
	}

	// Don't render if modal shouldn't be shown
	if (!show) {
		return null;
	}

	return (
		<Modal
			show={show}
			backdrop="static"
			keyboard={false}
			centered
			size="lg"
			className="modern-modal privacy-modal"
		>
			<div className="privacy-modal-overlay">
				<div className="privacy-modal-content">
					{/* Header */}
					<div className="privacy-header">
						<div className="header-icon">
							<i className="bi bi-shield-check"></i>
						</div>
						<div className="header-content">
							<h2>Welcome to Banking Intelligence</h2>
							<p>Please review and accept our policies to continue</p>
						</div>
						{/* Debug toggle - only in development */}
						{process.env.NODE_ENV === 'development' && (
							<button
								onClick={handleDebugToggle}
								style={{
									position: 'absolute',
									top: '10px',
									right: '10px',
									background: 'none',
									border: '1px solid #ccc',
									fontSize: '12px',
									padding: '2px 6px'
								}}
							>
								Debug
							</button>
						)}
					</div>

					{/* Debug Panel - Development Only */}
					{debugMode && process.env.NODE_ENV === 'development' && (
						<div style={{
							background: '#f8f9fa',
							border: '1px solid #dee2e6',
							padding: '10px',
							margin: '10px',
							fontSize: '12px',
							fontFamily: 'monospace'
						}}>
							<h5>Debug Info</h5>
							<div>User ID: {user?.id}</div>
							<div>Email: {user?.email}</div>
							<div>Privacy Accepted: {String(user?.privacyPolicyAccepted)} ({typeof user?.privacyPolicyAccepted})</div>
							<div>Terms Accepted: {String(user?.termsOfServiceAccepted)} ({typeof user?.termsOfServiceAccepted})</div>
							<div>Profile Error: {String(user?.profileLoadError)}</div>
							<div>Profile Complete: {String(isProfileComplete ? isProfileComplete() : 'N/A')}</div>
							<button onClick={handleForceRefreshProfile} style={{ marginTop: '5px', fontSize: '10px' }}>
								Force Profile Refresh
							</button>
						</div>
					)}

					{/* Body */}
					<div className="privacy-body">
						{error && (
							<div className="privacy-alert error">
								<i className="bi bi-exclamation-triangle-fill"></i>
								<span>{error}</span>
							</div>
						)}

						{/* Show profile loading error message if applicable */}
						{user?.profileLoadError && (
							<div className="privacy-alert warning">
								<i className="bi bi-exclamation-circle-fill"></i>
								<span>We're having trouble loading your account settings. Please ensure you accept both policies below.</span>
							</div>
						)}

						<div className="welcome-message">
							<h3>Your Privacy Matters</h3>
							<p>
								Before you start using Banking Intelligence, we want to be transparent about
								how we handle your data and what you can expect from our service.
							</p>
						</div>

						<div className="policy-sections">
							<div className="policy-section">
								<div className="section-header">
									<div className="section-icon">
										<i className="bi bi-shield-lock"></i>
									</div>
									<div className="section-content">
										<h4>Privacy & Data Protection</h4>
										<p>Learn how we collect, use, and protect your financial information</p>
									</div>
								</div>
								<div className="section-actions">
									<button
										className="view-policy-btn"
										onClick={handleViewPrivacyPolicy}
									>
										<i className="bi bi-eye"></i>
										View Privacy Policy
									</button>
								</div>
							</div>

							<div className="policy-section">
								<div className="section-header">
									<div className="section-icon">
										<i className="bi bi-file-text"></i>
									</div>
									<div className="section-content">
										<h4>Terms of Service</h4>
										<p>Understand your rights and responsibilities when using our platform</p>
									</div>
								</div>
								<div className="section-actions">
									<button
										className="view-policy-btn"
										onClick={handleViewTermsOfService}
									>
										<i className="bi bi-eye"></i>
										View Terms
									</button>
								</div>
							</div>
						</div>

						<div className="key-points">
							<h4>Key Points:</h4>
							<div className="points-grid">
								<div className="point-item">
									<div className="point-icon">
										<i className="bi bi-lock"></i>
									</div>
									<span>Your data is encrypted and secure</span>
								</div>
								<div className="point-item">
									<div className="point-icon">
										<i className="bi bi-x-circle"></i>
									</div>
									<span>We never sell your personal information</span>
								</div>
								<div className="point-item">
									<div className="point-icon">
										<i className="bi bi-person-check"></i>
									</div>
									<span>You control your data retention settings</span>
								</div>
								<div className="point-item">
									<div className="point-icon">
										<i className="bi bi-download"></i>
									</div>
									<span>Export your data anytime</span>
								</div>
							</div>
						</div>

						<div className="acceptance-section">
							<div className="checkbox-group">
								<label className="modern-checkbox">
									<input
										type="checkbox"
										checked={hasAcceptedPrivacy}
										onChange={(e) => setHasAcceptedPrivacy(e.target.checked)}
									/>
									<span className="checkmark"></span>
									<span className="checkbox-text">
										I have read and accept the <button type="button" className="link-btn" onClick={handleViewPrivacyPolicy}>Privacy Policy</button>
									</span>
								</label>

								<label className="modern-checkbox">
									<input
										type="checkbox"
										checked={hasAcceptedTerms}
										onChange={(e) => setHasAcceptedTerms(e.target.checked)}
									/>
									<span className="checkmark"></span>
									<span className="checkbox-text">
										I have read and accept the <button type="button" className="link-btn" onClick={handleViewTermsOfService}>Terms of Service</button>
									</span>
								</label>
							</div>
						</div>
					</div>

					{/* Footer */}
					<div className="privacy-footer">
						<div className="footer-note">
							<i className="bi bi-info-circle"></i>
							<span>You must accept both policies to use Banking Intelligence</span>
						</div>
						<div className="footer-actions">
							<button
								className="accept-btn"
								onClick={handleAcceptAll}
								disabled={loading || !hasAcceptedPrivacy || !hasAcceptedTerms}
							>
								{loading ? (
									<>
										<div className="btn-spinner"></div>
										<span>Processing...</span>
									</>
								) : (
									<>
										<i className="bi bi-check-circle"></i>
										<span>Accept & Continue</span>
									</>
								)}
							</button>
						</div>
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default PrivacyAgreementModal;